<template>
  <div>
    <!-- Lifecysle Messages -->
    <div class="mb-0">
      <b-card class="mb-2" v-if="productState === 'matured' && !hasLifecycleData">
        <div class="text-center py-3">
          <p class="text-muted mb-0">This product reached maturity on {{ formattedTerminationDate }}.</p>
          <p class="text-muted mb-0">No lifecycle data is available for this product.</p>
        </div>
      </b-card>

      <!-- Maturity outcome table -->
      <b-card v-if="productState === 'matured'" no-body class="mb-2">
        <div class="card-body pb-0">
          <b-card-title class="mb-0">Maturity Outcome</b-card-title>
          <p v-if="hasValidMaturityInfo" class="text-muted mb-0">Final outcome and payoff details for this product at maturity.</p>
        </div>
        <b-table
          v-if="hasValidMaturityInfo"
          :fields="maturityTableFields"
          :items="maturityTableItems"
          responsive
          :thead-class="''"
        />
        <b-card-body v-else>
          <div class="d-flex align-items-center justify-content-center py-3 text-muted">
            <feather-icon icon="AlertCircleIcon" size="16" class="mr-75" />
            <span>No data available</span>
          </div>
        </b-card-body>
      </b-card>

      <!-- Content for live state - no data message -->
      <b-card class="mb-2" v-else-if="productState === 'live' && !hasLifecycleData">
        <div class="text-center py-3">
          <p class="text-muted mb-0">There is no live data for this product.</p>
        </div>
      </b-card>

      <!-- Content for current and 'earl_live' (pre-strike) - no data message -->
      <b-card class="mb-2" v-else-if="(productState === 'current' || productState === 'early_live') && !hasLifecycleData">
        <div class="text-center py-3">
          <p class="text-muted mb-0">There is no data available yet for this product.</p>
        </div>
      </b-card>

      <!-- Content for live or matured state - with data -->
      <template v-if="hasLifecycleData">
        <b-row class="match-height mb-0">
          <b-col
            cols="12"
            md="6"
          >
            <b-card no-body>
              <template #header>
                <b-card-title>{{ underlyingAssetTitle }}</b-card-title>
              </template>
              <b-table
                v-if="underlyingTableContent.length > 0"
                :class="{ 'h-160 custom-scrollbar': underlyingTableContentLength > 3, 'product-snapshot-table': true }"
                responsive
                :sticky-header="true"
                :fields="underlyingTableHeader"
                :items="underlyingTableContent"
              />
              <b-card-body v-else>
                <div class="d-flex align-items-center justify-content-center py-3 text-muted">
                  <feather-icon icon="AlertCircleIcon" size="16" class="mr-75" />
                  <span>No data available</span>
                </div>
              </b-card-body>
            </b-card>
          </b-col>

          <b-col
            cols="12"
            md="6"
          >
            <template v-if="productSnapshotContent.length > 0">
              <report-table-card
                title="Product Snapshot"
                explanation="Key product features and their current status"
                table-header
                :fields="productSnapshotHeader"
                :items="productSnapshotContent"
              />
            </template>
            <b-card v-else no-body>
              <template #header>
                <b-card-title>Product Snapshot</b-card-title>
              </template>
              <b-card-body>
                <div class="d-flex align-items-center justify-content-center py-3 text-muted">
                  <feather-icon icon="AlertCircleIcon" size="16" class="mr-75" />
                  <span>No data available</span>
                </div>
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>

        <events-chart
          ref="eventsChart"
          :product-id="productId"
          :capital-data="capitalData"
          :product-state="productState"
          @loading-changed="$emit('events-loading-changed', $event)"
        />
      </template>
    </div>
  </div>
</template>

<script>
import {
  BCard, BCardTitle, BCardBody, BRow, BCol, BTable,
} from 'bootstrap-vue'
import ReportTableCard from '@/views/components/ReportTableCard.vue'
import EventsChart from '@/views/products/breakdown/charts/EventsEChart.vue'

export default {
  components: {
    BCard,
    BCardTitle,
    BCardBody,
    BRow,
    BCol,
    BTable,
    ReportTableCard,
    EventsChart,
  },
  props: {
    underlyingTable: {
      type: Object,
      default: () => ({
        header: [],
        content: [],
      }),
    },
    productSnapshot: {
      type: Object,
      default: () => ({
        header: [],
        content: [],
      }),
    },
    productId: {
      type: [Number, String],
      required: true,
    },
    hasLifecycleData: {
      type: Boolean,
      default: true,
    },
    productState: {
      type: String,
      default: 'current',
    },
    terminationDate: {
      type: String,
      default: null,
    },
    capitalData: {
      type: Object,
      default: () => ({
        SHOWDATA: {
          DATA: [],
        },
      }),
    },
    maturityInfo: {
      type: Object,
      default: null,
    },
  },
  methods: {
    formatOutcome(value) {
      if (!value) return ''
      const cleaned = value.replace(/date/gi, '')
      return cleaned.charAt(0).toUpperCase() + cleaned.slice(1)
    },
    collectCharts() {
      if (this.$refs.eventsChart && this.$refs.eventsChart.collectCharts) {
        return this.$refs.eventsChart.collectCharts()
      }
      return {}
    },
    collectData() {
      if (this.$refs.eventsChart && this.$refs.eventsChart.collectData) {
        return this.$refs.eventsChart.collectData()
      }
      return {}
    },
  },
  computed: {
    hasValidMaturityInfo() {
      return !!(this.maturityInfo
        && this.maturityInfo.maturitypayoff != null
        && this.maturityInfo.totalpayoff != null
        && this.maturityInfo.terminationoutcome != null)
    },
    maturityTableFields() {
      if (!this.maturityInfo) return []
      const fields = [
        { key: 'terminationdate', label: 'Termination Date' },
        { key: 'terminationoutcome', label: 'Outcome' },
        { key: 'maturitypayoff', label: 'Termination Value' },
        { key: 'totalpayoff', label: 'Total Payoff' },
      ]
      if (this.maturityInfo.income != null && this.maturityInfo.income !== 0) {
        fields.splice(3, 0, { key: 'income', label: 'Total Cashflows' })
      }
      return fields
    },
    maturityTableItems() {
      if (!this.maturityInfo) return []
      const item = {
        terminationdate: this.formattedTerminationDate,
        terminationoutcome: this.formatOutcome(this.maturityInfo.terminationoutcome),
        maturitypayoff: `${this.maturityInfo.maturitypayoff}`,
        totalpayoff: `${this.maturityInfo.totalpayoff}`,
      }
      if (this.maturityInfo.income != null && this.maturityInfo.income !== 0) {
        item.income = `${this.maturityInfo.income}`
      }
      return [item]
    },
    formattedTerminationDate() {
      if (!this.terminationDate) return ''
      const date = new Date(this.terminationDate)
      const day = date.getDate()
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      const month = months[date.getMonth()]
      const year = date.getFullYear()
      return `${day} ${month} ${year}`
    },
    underlyingTableHeader() {
      return this.underlyingTable && this.underlyingTable.header
        ? this.underlyingTable.header
        : []
    },
    underlyingTableContent() {
      return this.underlyingTable && this.underlyingTable.content
        ? this.underlyingTable.content
        : []
    },
    underlyingTableContentLength() {
      return this.underlyingTableContent.length
    },
    underlyingAssetTitle() {
      const count = this.underlyingTableContentLength
      return `Underlying Asset${count > 1 ? 's' : ''}`
    },
    productSnapshotHeader() {
      return this.productSnapshot && this.productSnapshot.header
        ? this.productSnapshot.header
        : []
    },
    productSnapshotContent() {
      return this.productSnapshot && this.productSnapshot.content
        ? this.productSnapshot.content
        : []
    },
  },
}
</script>

<style lang="scss">
.dark-layout .product-snapshot-table th {
  color: #d0d2d6 !important;
}
</style>

<style lang="scss" scoped>
.h-160 {
  max-height: 160px;
}
.custom-scrollbar::-webkit-scrollbar {
  width: 14px;
}

.header-card {
  margin-bottom: calc(2rem + 1px) !important;
}
</style>
